import type { TimelineSpeedPointDict } from '../../../../generated/nodeDataTypes'
import type { TimelineClip } from './types'

export type SpeedPoint = TimelineSpeedPointDict
export const SPEED_CURVE_PRESETS: Record<string, SpeedPoint[]> = {
  None: [{ position: 0, speed: 1 }, { position: 1, speed: 1 }],
  Montage: [{ position: 0, speed: 1 }, { position: .3, speed: 3 }, { position: .6, speed: .3 }, { position: 1, speed: 1 }],
  Hero: [{ position: 0, speed: 1 }, { position: .25, speed: 2 }, { position: .5, speed: .25 }, { position: .75, speed: 2 }, { position: 1, speed: 1 }],
  Bullet: [{ position: 0, speed: 1 }, { position: .35, speed: 1 }, { position: .5, speed: .2 }, { position: .65, speed: 1 }, { position: 1, speed: 1 }],
  'Jump cut': [{ position: 0, speed: 1 }, { position: .35, speed: 1 }, { position: .5, speed: 5 }, { position: .65, speed: 1 }, { position: 1, speed: 1 }],
  'Flash in': [{ position: 0, speed: 4 }, { position: .3, speed: 4 }, { position: .65, speed: 1 }, { position: 1, speed: 1 }],
  'Flash out': [{ position: 0, speed: 1 }, { position: .35, speed: 1 }, { position: .7, speed: 4 }, { position: 1, speed: 4 }],
}

export function spanDuration(width: number, first: number, last: number): number {
  return Math.abs(last - first) < 1e-8 ? width / first : width * Math.log(last / first) / (last - first)
}

/** Normalized source position → seconds in the rendered curve. */
export function curveTime(points: SpeedPoint[], duration: number, position = 1): number {
  let elapsed = 0
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1], b = points[i]
    if (position <= a.position) break
    const width = Math.min(position, b.position) - a.position
    const last = a.speed + (b.speed - a.speed) * width / (b.position - a.position)
    elapsed += spanDuration(width * duration, a.speed, last)
  }
  return elapsed
}

export function curvePosition(points: SpeedPoint[], time: number): number {
  let remaining = time
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1], b = points[i], width = b.position - a.position
    const span = spanDuration(width, a.speed, b.speed)
    if (remaining <= span) {
      const slope = (b.speed - a.speed) / width
      return a.position + (Math.abs(slope) < 1e-8 ? remaining * a.speed : a.speed * Math.expm1(remaining * slope) / slope)
    }
    remaining -= span
  }
  return 1
}

export function speedAt(points: SpeedPoint[], position: number): number {
  const i = points.findIndex((point) => point.position >= position)
  if (i <= 0) return points[i === 0 ? 0 : points.length - 1].speed
  const a = points[i - 1], b = points[i]
  return a.speed + (b.speed - a.speed) * (position - a.position) / (b.position - a.position)
}

/** Crop a previously baked curve back to its original source after split/trim. */
export function editableCurve(clip: TimelineClip) {
  const curve = clip.speedCurve
  if (!curve) return { sourcePath: clip.sourcePath, start: clip.trimStart,
    end: clip.originalDuration - clip.trimEnd, points: SPEED_CURVE_PRESETS.None }
  const normalizedDuration = curveTime(curve.points, 1)
  const from = curvePosition(curve.points, clip.trimStart / curve.render_duration * normalizedDuration)
  const to = curvePosition(curve.points, (clip.originalDuration - clip.trimEnd) / curve.render_duration * normalizedDuration)
  const width = curve.source_end - curve.source_start
  return { sourcePath: curve.source_path, start: curve.source_start + from * width,
    end: curve.source_start + to * width, points: [
      { position: 0, speed: speedAt(curve.points, from) },
      ...curve.points.filter((p) => p.position > from + .01 * (to - from) && p.position < to - .01 * (to - from))
        .map((p) => ({ position: (p.position - from) / (to - from), speed: p.speed })),
      { position: 1, speed: speedAt(curve.points, to) },
    ] }
}

export function curveMarkers(clip: TimelineClip) {
  const curve = clip.speedCurve
  if (!curve) return []
  const total = curveTime(curve.points, 1)
  return curve.points.map((point) => ({ speed: point.speed * clip.speed,
    time: (curveTime(curve.points, 1, point.position) / total * curve.render_duration - clip.trimStart) / clip.speed,
  })).filter((point) => point.time >= 0 && point.time <= clip.effectiveDuration)
}
